import store from "@/store";
import router from "@/router";
import { CLEAR_ERRORMSG, LOGOUT, UPDATE_ERRORMSG } from "@/store/action_types";
import { AXIOS_TIMEOUT, SERVER_ERR_MSG } from "@/utils/const";
import { resetLoaders } from "@/utils/loaders";
import axios, { AxiosInstance } from "axios";
import { LOGIN_ROUTE } from "@/router/routes";

const baseAPI: AxiosInstance = axios.create({
  baseURL: `${process.env.NODE_ENV == "development" ? process.env.VUE_APP_DEV_SERVER_URL : process.env.VUE_APP_SERVER_URL}/${process.env.VUE_APP_VERSION}`,
  // baseURL: `${process.env.VUE_APP_SERVER_URL}/${process.env.VUE_APP_VERSION}`,
  headers: {
    "Content-type": "application/json",
  },
  timeout: AXIOS_TIMEOUT,
});

baseAPI.interceptors.request.use(
  (request) => {
    const token = store.getters.sessionToken;
    if (token) request.headers.Authorization = `Bearer ${token}`;
    return request;
  },
  (requestError) => {
    // TODO: add error handling for when network disappears
    return requestError;
  },
);

baseAPI.interceptors.response.use(
  (response) => {
    store.dispatch(CLEAR_ERRORMSG);
    return response;
  },
  function (error) {
    try {
      console.log("error => ", error);
      console.log("error.code => ", error.code === "ECONNABORTED");

      // clear error messages
      resetLoaders();

      // Do something with response error.
      // Guard every branch with optional chaining so this handler can never
      // itself throw (e.g. when there is no response) and discard the real error.
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;

      // Auth endpoints (login / signup / continue-with-google / password reset)
      // own their own error UX. A 401/403 here means "those credentials were
      // rejected", NOT "your session expired" — so we must never log the user
      // out or bounce them to /login from these. Doing so is what made a failed
      // Google sign-in silently redirect to the login page after consent.
      const requestUrl = error.config?.url || "";
      const isAuthEndpoint = requestUrl.includes("/auth/");

      if (error.code === "ECONNABORTED") {
        store.dispatch(UPDATE_ERRORMSG, {
          errorMsg: "Error! Please check your connection. ECONNABORTED.",
        });
      } else if (!error.response) {
        // No response at all: network down, CORS blocked, or server unreachable.
        store.dispatch(UPDATE_ERRORMSG, {
          errorMsg: "Could not reach the server. Please check your connection and try again.",
        });
      }

      if (status === 400) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
        // store.dispatch(LOGOUT_LOCALLY)
      }

      // 402 Payment Required — used for plan/seat limits (e.g. "upgrade to add
      // more team members"). Surface the server's message; never log out.
      if (status === 402) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
      }

      if (status === 401) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
        if (!isAuthEndpoint) {
          store.dispatch(LOGOUT);
          router.push({ name: LOGIN_ROUTE });
        }
      }

      if (status === 403) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
        if (!isAuthEndpoint) {
          store.dispatch(LOGOUT);
          router.push({ name: LOGIN_ROUTE });
        }
      }

      if (status === 404) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
        // router.push({ name: '404' })
      }

      if (status === 409) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
      }

      // 422 (receipt could not be confirmed) and 502 (bank unreachable) are
      // returned by the manual verifier — surface their reason to the user.
      if (status === 422) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg });
      }

      if (status === 500 || status === 502) {
        store.dispatch(UPDATE_ERRORMSG, { errorMsg: serverMsg || SERVER_ERR_MSG });
      }
    } catch (e) {
      return Promise.reject(error);
    }
  },
);

export default baseAPI;
